import React, { useEffect, useMemo, useRef, useState } from "react";
import { getLocalizedString } from "@icarius-localization/strings";
import {
    IconButton,
    Card,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Typography,
} from "@material-ui/core";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import { IMAGES_ENDPOINT } from "@icarius-connection/endpoints";
import { googleChartColors, shadeHexColor } from "@icarius-utils/colors";
import DefaultImage from "@icarius-assets/images/no_profile_dark.png";
import ForceGraph2D from "react-force-graph-2d";
import { forceX, forceY } from "d3-force";
import { useDispatch } from "react-redux";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { Alert } from "@material-ui/lab";
import { openDialogAction } from "@icarius-common/dialog/actions";
import ProfileImg from "@icarius-common/profileImg";

const IMAGE_SIZE = 10;
const NODE_VISUALIZATION_OPTIONS = {
    image: "image",
    name: "name",
}
const TYPE_OPTIONS = {
    centrality: "centrality",
    scope: "scope",
}

const getLinkIds = (link) => {
    // el componente modifica el array y puede venir como objeto o solo el id
    return {
        sourceId: typeof link.source === "object" ? link.source.id : link.source,
        targetId: typeof link.target === "object" ? link.target.id : link.target,
    }
}

const OnaChart = ({ data }) => {

    const [zoom, setZoom] = useState(1);
    const [type, setType] = useState(TYPE_OPTIONS.centrality);
    const [nodeVisualization, setNodeVisualization] = useState(NODE_VISUALIZATION_OPTIONS.image);
    const [collaboratorFilter, setCollaboratorFilter] = useState("");
    const [frequencyFilter, setFrequencyFilter] = useState("");
    const chartRef = useRef();

    const dispatch = useDispatch();

    const dataToUseByType = useMemo(() => {
        if (type === TYPE_OPTIONS.centrality) return data.centralityData;
        if (type === TYPE_OPTIONS.scope) return data.scopeData;
    }, [type, data])

    const participantList = useMemo(() => { // personas que respondieron la encuesta
        const sourceIds = new Set(dataToUseByType.links.map((link) => getLinkIds(link).sourceId));
        return dataToUseByType.nodes.filter((node) => sourceIds.has(node.id));
    }, [dataToUseByType])

    const filteredGraphData = useMemo(() => {
        if (!collaboratorFilter && !frequencyFilter) return dataToUseByType;

        const filteredLinks = dataToUseByType.links.filter((link) => {
            const matchesCollaborator = !collaboratorFilter || getLinkIds(link).sourceId === collaboratorFilter;
            const matchesFrequency = !frequencyFilter || link.weightDescription === frequencyFilter;
            return matchesCollaborator && matchesFrequency;
        });

        const filteredSourceIds = new Set(filteredLinks.map((link) => getLinkIds(link).sourceId));
        const filteredTargetIds = new Set(filteredLinks.map((link) => getLinkIds(link).targetId));
        const filteredNodes = dataToUseByType.nodes.filter((node) => filteredTargetIds.has(node.id) || filteredSourceIds.has(node.id));

        return {
            nodes: filteredNodes,
            links: filteredLinks,
        };
    }, [dataToUseByType, collaboratorFilter, frequencyFilter])

    const imageCache = useMemo(() => {
        const auxCache = {};
        filteredGraphData.nodes.forEach((node) => {
            const img = new Image();
            img.src = node.image ? IMAGES_ENDPOINT + node.image : DefaultImage;
            img.onerror = () => {
                img.src = DefaultImage;
            };
            auxCache[node.id] = img;
        });
        return auxCache;
    }, [filteredGraphData.nodes]);

    const nodeColorMap = useMemo(() => {
        const scopes = [...new Set(filteredGraphData.nodes.map(n => n.scope))];
        return Object.fromEntries(scopes.map((scope, i) => [scope, googleChartColors[i % googleChartColors.length]]));
    }, [filteredGraphData.nodes]);

    useEffect(() => {
        if (!chartRef.current) return;

        // hacer que los grupos de nodos esten mas cerca entre si
        const strength = 0.5;
        chartRef.current.d3Force("x", forceX(0).strength(strength));
        chartRef.current.d3Force("y", forceY(0).strength(strength));

        // para separar los nodos de un mismo grafo
        chartRef.current.d3Force("charge").strength(-90);

        setTimeout(() => {
            if (!chartRef.current) return;
            chartRef.current.zoomToFit(400, 50);
        }, 500);
    }, [filteredGraphData]);

    const handleZoomIn = () => {
        const newZoom = zoom * 1.5;
        setZoom(newZoom);
        chartRef.current.zoom(newZoom, 300);
    };

    const handleZoomOut = () => {
        const newZoom = zoom / 1.5;
        setZoom(newZoom);
        chartRef.current.zoom(newZoom, 300);
    };

    const getNodeColor = (node) => nodeColorMap[node.scope] ?? "#fff";
    const getNodeTextToShow = (node) => node.fullName;

    const getNameNodeDimensions = (text, ctx) => {
        const fontSize = 2;
        const padding = { x: 2, y: 1.5 };
        ctx.font = `${fontSize}px Roboto`;
        const textWidth = ctx.measureText(text).width;
        return {
            fontSize,
            nodeWidth: textWidth + padding.x * 2,
            nodeHeight: fontSize + padding.y * 2,
        };
    };

    const renderTextNode = (node, ctx, globalScale) => {
        const text = getNodeTextToShow(node);
        const { fontSize, nodeWidth, nodeHeight } = getNameNodeDimensions(text, ctx);

        const x = node.x - nodeWidth / 2;
        const y = node.y - nodeHeight / 2;
        const radius = 1;

        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + nodeWidth - radius, y);
        ctx.quadraticCurveTo(x + nodeWidth, y, x + nodeWidth, y + radius);
        ctx.lineTo(x + nodeWidth, y + nodeHeight - radius);
        ctx.quadraticCurveTo(x + nodeWidth, y + nodeHeight, x + nodeWidth - radius, y + nodeHeight);
        ctx.lineTo(x + radius, y + nodeHeight);
        ctx.quadraticCurveTo(x, y + nodeHeight, x, y + nodeHeight - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();

        ctx.fillStyle = "#fff";
        ctx.fill();

        ctx.strokeStyle = getNodeColor(node);
        ctx.lineWidth = 3 / globalScale;
        ctx.stroke();

        ctx.font = `${fontSize}px Roboto`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#000";
        ctx.fillText(text, node.x, node.y);
    };

    const renderImageNode = (node, ctx, globalScale) => {
        const { x, y, id } = node;
        const img = imageCache[id];
        const size = IMAGE_SIZE;

        if (img && img.complete && img.naturalWidth !== 0) {
            ctx.save();
            ctx.beginPath();
            ctx.arc(x, y, size / 2, 0, 2 * Math.PI, false);
            ctx.clip();

            ctx.drawImage(img, x - size / 2, y - size / 2, size, size);

            ctx.restore();

            ctx.beginPath();
            ctx.arc(x, y, size / 2, 0, 2 * Math.PI, false);
            ctx.lineWidth = 3 / globalScale;
            ctx.strokeStyle = ctx.fillStyle = getNodeColor(node);
            ctx.stroke();
        }
    };

    const renderNode = (node, ctx, globalScale) => {
        if (nodeVisualization === NODE_VISUALIZATION_OPTIONS.name) renderTextNode(node, ctx, globalScale);
        if (nodeVisualization === NODE_VISUALIZATION_OPTIONS.image) renderImageNode(node, ctx, globalScale);
    }

    const nodePointerAreaPaint = (node, color, ctx) => {
        if (nodeVisualization === NODE_VISUALIZATION_OPTIONS.name) {
            const text = getNodeTextToShow(node);
            const { nodeWidth, nodeHeight } = getNameNodeDimensions(text, ctx);
            ctx.fillStyle = color;
            ctx.fillRect(
                node.x - nodeWidth / 2,
                node.y - nodeHeight / 2,
                nodeWidth,
                nodeHeight
            );
        }

        if (nodeVisualization === NODE_VISUALIZATION_OPTIONS.image) {
            const size = IMAGE_SIZE;
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.arc(node.x, node.y, size / 2, 0, 2 * Math.PI, false);
            ctx.fill();
        }
    };

    const handleSetType = (newType) => {
        if (dataToUseByType.nodes.some((node) => !node.scope)) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("cannotShowScope"), severity: "info", duration: null }));
            return;
        }

        setType(newType);
        setNodeVisualization(newType === TYPE_OPTIONS.scope ? NODE_VISUALIZATION_OPTIONS.name : NODE_VISUALIZATION_OPTIONS.image);
        setCollaboratorFilter("");
    }

    const handleNodeClick = (node) => {
        if (node.firstName === node.fullName) return; // son nodos de alcance, no de persona
        dispatch(openDialogAction({
            title: getLocalizedString("collabData"),
            acceptOnly: true,
            msg: (
                <div
                    style={{
                        flexDirection: "column",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 5,
                    }}
                >
                    <ProfileImg
                        image={node.image}
                        size={150}
                    />
                    <Typography className="whiteText" style={{ marginTop: 10, fontSize: 20 }}>
                        {node.fullName}
                    </Typography>
                    <Typography className="whiteText" style={{ fontSize: 14 }}>
                        {node.scope}
                    </Typography>
                </div>
            )
        }))
    }

    return (
        <Card
            style={{
                position: "relative",
                overflow: "hidden",
            }}
        >
            <div
                style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 30,
                    padding: 15,
                    background: "var(--tertiaryBackgroundColor)",
                    borderBottom: "1px solid var(--borderColor)",
                }}
            >
                <Grid container item xs={12} spacing={2}>
                    <Grid container item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel shrink id={'label-type'}>
                                {getLocalizedString("type")}
                            </InputLabel>
                            <Select
                                displayEmpty
                                value={type}
                                labelId={'label-type'}
                                id={'select-type'}
                                onChange={(e) => handleSetType(e.target.value)}
                                margin={"none"}
                            >
                                {
                                    Object.values(TYPE_OPTIONS).map((item) => (
                                        <MenuItem
                                            className={"whiteText"}
                                            key={item}
                                            value={item}
                                        >
                                            {getLocalizedString(item)}
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid container item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel shrink id={'label-collaborator'}>
                                {getLocalizedString("participant")}
                            </InputLabel>
                            <Select
                                displayEmpty
                                value={collaboratorFilter}
                                labelId={'label-collaborator'}
                                id={'select-collaborator'}
                                onChange={(e) => setCollaboratorFilter(e.target.value)}
                                margin={"none"}
                            >
                                <MenuItem className={"whiteText"} value={""}>
                                    {getLocalizedString("allMale")}
                                </MenuItem>
                                {
                                    participantList?.map((item) => (
                                        <MenuItem
                                            className={"whiteText"}
                                            key={item.id}
                                            value={item.id}
                                        >
                                            {item.fullName}
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    {
                        type === TYPE_OPTIONS.centrality &&
                        <>
                            <Grid container item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel shrink id={'label-nodeVisualization'}>
                                        {getLocalizedString("nodeVisualization")}
                                    </InputLabel>
                                    <Select
                                        displayEmpty
                                        value={nodeVisualization}
                                        labelId={'label-nodeVisualization'}
                                        id={'select-nodeVisualization'}
                                        onChange={(e) => setNodeVisualization(e.target.value)}
                                        margin={"none"}
                                    >
                                        {
                                            Object.values(NODE_VISUALIZATION_OPTIONS).map((item) => (
                                                <MenuItem
                                                    className={"whiteText"}
                                                    key={item}
                                                    value={item}
                                                >
                                                    {getLocalizedString(item)}
                                                </MenuItem>
                                            ))
                                        }
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid container item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel shrink id={'label-frequency'}>
                                        {getLocalizedString("frequency")}
                                    </InputLabel>
                                    <Select
                                        displayEmpty
                                        value={frequencyFilter}
                                        labelId={'label-frequency'}
                                        id={'select-frequency'}
                                        onChange={(e) => setFrequencyFilter(e.target.value)}
                                        margin={"none"}
                                    >
                                        <MenuItem className={"whiteText"} value={""}>
                                            {getLocalizedString("allMale")}
                                        </MenuItem>
                                        {
                                            data.centralityData.frequencyList.map((item, index) => (
                                                <MenuItem
                                                    className={"whiteText"}
                                                    key={index}
                                                    value={item}
                                                >
                                                    {item}
                                                </MenuItem>
                                            ))
                                        }
                                    </Select>
                                </FormControl>
                            </Grid>
                        </>
                    }
                </Grid>
                <div style={{ flexShrink: 0 }}>
                    <IconButton onClick={handleZoomOut}>
                        <ZoomOutIcon style={{ fill: "var(--mainText)" }} />
                    </IconButton>
                    <IconButton onClick={handleZoomIn}>
                        <ZoomInIcon style={{ fill: "var(--mainText)" }} />
                    </IconButton>
                </div>
            </div>
            {
                Boolean(filteredGraphData.nodes.length) ?
                    <div style={{ cursor: "grab" }}>
                        <ForceGraph2D
                            ref={chartRef}
                            graphData={filteredGraphData}
                            onZoom={({ k }) => setZoom(k)}
                            nodeLabel={(node) => node.fullName}
                            nodeColor={getNodeColor}
                            linkCurvature={0.05}
                            linkColor={({ source }) => shadeHexColor(getNodeColor(source), 0.35)}
                            linkLabel={({ weightDescription }) => weightDescription}
                            linkWidth={({ weight }) => {
                                if (weight === 1) return 1;
                                if (weight < 6) return 5;
                                return 10;
                            }}
                            linkDirectionalArrowLength={7}
                            linkDirectionalArrowRelPos={1}
                            nodeCanvasObject={renderNode}
                            nodePointerAreaPaint={nodePointerAreaPaint}
                            onNodeClick={handleNodeClick}
                        />
                    </div>
                    :
                    <div style={{ width: "100%", padding: "20px 14px" }}>
                        <Alert variant="filled" severity="info">
                            {getLocalizedString("noDataForFilter")}
                        </Alert>
                    </div>
            }
        </Card>
    );
}

export default OnaChart;